/**
 * Pure-функция для генерации шаблона сообщения магазину (Экраны 2.4.4 / 2.2.X / 2.3.X).
 * PRD §06 + ADR-0007 «Shops shared module».
 *
 * Не содержит ПДн пользователя — только название товара и Global_ID для корреляции
 * (P-009 минимизация ПДн). Длина < 2000 символов (лимит WhatsApp).
 *
 * Языки: для MVP все города → 'ru'. KZ/UZ переводы появятся в Phase 12+ когда
 * будет i18n инфраструктура (Lingui). Параметр `language` оставлен для
 * расширения без изменения сигнатуры.
 */
export type Language = 'ru' | 'uz' | 'kz';

/**
 * Полный union поддерживаемых kind'ов сообщения. ShopMessageKind ≠ entityKind:
 * один entityKind может иметь несколько ShopMessageKind (handle → 'handle'|'hook',
 * decor → 'decor-facade'|'decor-countertop'|'decor-panel'), потому что текст
 * шаблона зависит от подтипа товара.
 *
 * Реализованы (Phase 8 step 2): handle, hook.
 * Реализуются в Phase 8 step 3: decor-facade, decor-countertop, decor-panel, paint.
 */
export type ShopMessageKind =
  | 'handle'
  | 'hook'
  | 'decor-facade'
  | 'decor-countertop'
  | 'decor-panel'
  | 'paint'
  | 'furniture'
  | 'partner';

export interface ShopMessageInput {
  kind: ShopMessageKind;
  productName: string;
  /**
   * Global_ID товара. С Фазы 6 в ТЕКСТ сообщения НЕ выводится (фидбек Aibek с
   * живого сайта — код пугает дилера). Оставлен в контракте для совместимости
   * вызывающих и возможной телеметрии.
   */
  globalId: string;
  language: Language;
  /**
   * Бренд каталога (verbatim, P-001) — слот «<изделие> <название>, <бренд>».
   * Для kind='paint' игнорируется (вместо бренда — RAL-код). Для остальных
   * kind'ов: если пуст — слот бренда опускается.
   */
  brand?: string;
  /**
   * Подкатегория декора (verbatim, P-001) — для специального ярлыка изделия.
   * Сейчас задействована только для kind='decor-facade' + subcategory='МДФ Пленка'
   * (решение Aibek 2026-06-09): ярлык «Пленка для фасадов» вместо «фасад» — иначе
   * на заглушке Greenwood (name='Фасады пленка') читается криво «фасад Фасады
   * пленка». Для handles/paint/прочих подкатегорий не задаётся → ветка не сработает.
   */
  subcategory?: string;
  /**
   * Чистый URL карточки товара (без query-фильтра). Фаза 6.1 (item 4): добавляется
   * в конец «. Смотрите по ссылке <URL>». Если пуст — хвост не добавляется.
   */
  productUrl?: string;
  /**
   * Обязателен для kind='paint', throws при отсутствии или несоответствии
   * формату `RAL NNNN`. Не используется для других kind'ов. Paint fixture хранит
   * ralCode с префиксом `'RAL 7038'`, и шаблон вставляет его без модификации
   * (см. ADR-0010 §2 и paint-shared.ts `RalCodeSchema`). Регексп — копия
   * `RAL_CODE_REGEX` из shared чтобы избежать кросс-package import.
   */
  ralCode?: string;
}

// Источник истины — packages/shared/src/contracts/paint-shared.ts:18
// (`RAL_CODE_REGEX`). Локальная копия чтобы не тащить shared contracts в
// presentation-слой shops. Любое изменение в shared должно отразиться здесь.
const SHOPS_RAL_CODE_REGEX = /^RAL \d{4}$/;

/**
 * 👋 заданный юникод-эскейпом, НЕ литералом (Фаза 6, решение Aibek): литеральный
 * эмодзи в исходнике один раз испортился при сборке («�»). Эскейп даёт корректный
 * codepoint U+1F44B (суррогатная пара) в рантайме; `encodeURIComponent` в
 * deeplink-билдерах кодирует его как валидный UTF-8.
 *
 * Фаза 6.1 (item 5): доказано — построенная wa.me-ссылка байт-корректна
 * (`%F0%9F%91%8B`, без двойного кодирования и одиночных суррогатов). «�» на проде
 * — клиент WhatsApp ломает ЛИДИРУЮЩИЙ 4-байтный эмодзи, поэтому 👋 ставим НЕ
 * первым символом: «Здравствуйте 👋, …».
 */
const WAVE = '\u{1F44B}';

/**
 * Спец-ярлык для подкатегории «МДФ Пленка» фасадов (решение Aibek 2026-06-09).
 * verbatim, заглавная «П», НЕ склонять. У Greenwood name='Фасады пленка' —
 * заглушка-дубль, её опускаем (иначе «Пленка для фасадов Фасады пленка»).
 */
const FACADE_FILM_SUBCATEGORY = 'МДФ Пленка'; // verbatim, без ё (P-001)
const FACADE_FILM_LABEL = 'Пленка для фасадов';
const FACADE_FILM_STUB_NAME = 'Фасады пленка'; // Greenwood name-заглушка

/**
 * Человеческое имя изделия per kind в винительном падеже («я ищу <X>»). Фаза 6:
 * decor-panel → «ЛДСП», фасад/столешница — по-человечески (не «декор фасада»).
 * paint — отдельный шаблон (RAL-код вместо бренда).
 */
const KIND_NOUN: Record<
  Language,
  Record<Exclude<ShopMessageKind, 'paint' | 'furniture' | 'partner'>, string>
> = {
  ru: {
    handle: 'ручку',
    hook: 'крючок',
    'decor-facade': 'фасад',
    'decor-countertop': 'столешницу',
    'decor-panel': 'ЛДСП',
  },
  uz: {
    handle: 'ручку',
    hook: 'крючок',
    'decor-facade': 'фасад',
    'decor-countertop': 'столешницу',
    'decor-panel': 'ЛДСП',
  }, // TODO(i18n): локализовать в Phase 12+
  kz: {
    handle: 'ручку',
    hook: 'крючок',
    'decor-facade': 'фасад',
    'decor-countertop': 'столешницу',
    'decor-panel': 'ЛДСП',
  },
};

/**
 * Текст запроса дилеру (Фаза 6, единый для всех блоков):
 * `👋 Здравствуйте, я ищу <изделие> <название>, <бренд> или похожий`.
 * Без Global_ID-кода. paint — RAL вместо бренда.
 */
export function shopMessage(input: ShopMessageInput): string {
  const { kind, productName, language, ralCode, brand, productUrl, subcategory } = input;
  const name = productName.length > 200 ? productName.slice(0, 200) + '…' : productName;
  // item 4: «. Смотрите по ссылке <URL>» в конце, если URL передан.
  const urlPart =
    productUrl && productUrl.trim() ? `. Смотрите по ссылке ${productUrl.trim()}` : '';

  if (kind === 'paint') {
    if (!ralCode || ralCode.trim() === '') {
      throw new Error("shopMessage: kind='paint' requires non-empty ralCode");
    }
    if (!SHOPS_RAL_CODE_REGEX.test(ralCode)) {
      throw new Error(
        `shopMessage: kind='paint' requires ralCode in format 'RAL NNNN' (got: ${JSON.stringify(ralCode)})`,
      );
    }
    return `Здравствуйте ${WAVE}, я ищу краску ${name}, ${ralCode} или похожий${urlPart}`;
  }

  // partner (Блок 8, D3): прямая связь с подрядчиком, БЕЗ товара/бренда/URL —
  // только приветствие (точный текст согласовали Партнёры↔Ручки, COORD 2026-06-14).
  // WAVE — константа (НЕ литерал, чтобы не «�» при сборке) и НЕ первый символ
  // (WhatsApp ломает лидирующий 4-байтный эмодзи). Ветка ВЫШЕ KIND_NOUN (по образцу
  // paint/furniture): у 'partner' нет noun-ключа в KIND_NOUN (расширен Exclude).
  if (kind === 'partner') {
    return `Здравствуйте ${WAVE}`;
  }

  const brandPart = brand && brand.trim() ? `, ${brand.trim()}` : '';

  // furniture (Блок 5, E7): БЕЗ noun-изделия (название самодостаточно), бренд =
  // brandName (гибрид). Формат прототипа: «я ищу <name>, <brandName> или похожий».
  if (kind === 'furniture') {
    return `Здравствуйте ${WAVE}, я ищу ${name}${brandPart} или похожий${urlPart}`;
  }

  // МДФ Пленка фасадов (решение Aibek): ярлык «Пленка для фасадов» вместо «фасад».
  // Greenwood-заглушка (name='Фасады пленка') → название опускаем (без дубля и
  // без двойного пробела/висячей запятой). Цветное название (AIF/Kira/EMC) — после.
  if (kind === 'decor-facade' && subcategory === FACADE_FILM_SUBCATEGORY) {
    const namePart = name === FACADE_FILM_STUB_NAME ? '' : ` ${name}`;
    return `Здравствуйте ${WAVE}, я ищу ${FACADE_FILM_LABEL}${namePart}${brandPart} или похожий${urlPart}`;
  }

  const noun = KIND_NOUN[language][kind];
  return `Здравствуйте ${WAVE}, я ищу ${noun} ${name}${brandPart} или похожий${urlPart}`;
}

/**
 * Маппинг город → язык интерфейса/сообщения. Источник: PRD §06 + уточнения.
 *
 * Для MVP все города → 'ru' (KZ/UZ переводы — Phase 12+, см. TODO в shopMessage).
 * Если когда-то появятся локализованные шаблоны, здесь же поменять значения.
 */
export const CITY_TO_LANGUAGE: Record<string, Language> = {
  // Казахстан
  Астана: 'ru',
  Алматы: 'ru',
  Шымкент: 'ru',
  Караганда: 'ru',
  // Россия
  Москва: 'ru',
  'Санкт-Петербург': 'ru',
  Казань: 'ru',
  Новосибирск: 'ru',
  // Узбекистан
  Ташкент: 'ru',
  Самарканд: 'ru',
};

export const CITIES = Object.freeze(Object.keys(CITY_TO_LANGUAGE));
